import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase, User } from 'src/lib/supabase';
import { authApi, setAuthToken, getAuthToken, setSupabaseTokenGetter } from 'src/services/api';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  devLogin: (email: string, password: string) => Promise<boolean>;
  isDevMode: boolean;
}

const isLocalhost = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeoutOccurred, setTimeoutOccurred] = useState(false);
  const [isDevLoggedIn, setIsDevLoggedIn] = useState(false);
  const isDevMode = isLocalhost();

  const fetchUserProfile = async (email: string, abortSignal?: AbortSignal): Promise<User | null> => {
    try {
      console.log('🔍 [fetchUserProfile] Starting for:', email);
      
      // الحصول على session أولاً
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || abortSignal?.aborted) {
        console.error('❌ Session error:', sessionError);
        return null;
      }

      if (!sessionData.session) {
        console.warn('⚠️ No session found');
        return null;
      }

      console.log('✅ Session found:', sessionData.session.user.email);
      console.log('🆔 User ID:', sessionData.session.user.id);

      const userId = sessionData.session.user.id;
      
      // إنشاء user object من session إذا فشل الـ query
      const fallbackUser: User = {
        id: userId,
        email: sessionData.session.user.email || email,
        full_name: sessionData.session.user.user_metadata?.full_name || null,
        phone: null,
        role: null,
        avatar_url: sessionData.session.user.user_metadata?.avatar_url || null,
        is_profile_complete: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // جرب query مع timeout صارم
      console.log('🚀 Querying by user ID:', userId);
      
      let timeoutId: NodeJS.Timeout | null = null;
      let timeoutOccurredLocal = false;

      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Timeout بعد 5 ثواني
      const timeoutPromise = new Promise<User | null>((resolve) => {
        timeoutId = setTimeout(() => {
          if (!abortSignal?.aborted) {
            console.error('⏱️ Query timeout - session expired, signing out...');
            timeoutOccurredLocal = true;
            setTimeoutOccurred(true);
            resolve(null);
          }
        }, 5000);
      });

      const result = await Promise.race([
        queryPromise.then((result) => {
          if (timeoutId) clearTimeout(timeoutId);
          return result;
        }),
        timeoutPromise
      ]) as any;

      // تنظيف timeout
      if (timeoutId) clearTimeout(timeoutId);

      // إذا كان timeout، نرجع null لتسجيل الخروج
      if (timeoutOccurredLocal || (result === null && timeoutOccurred)) {
        console.error('❌ Timeout occurred - signing out');
        return null;
      }

      if (abortSignal?.aborted) {
        return null;
      }

      const { data, error } = result || {};

      console.log('📊 Query result:', { data, error });

      if (error) {
        console.error('❌ Query error:', error);
        // إذا كان error بسبب network timeout أو connection، نسجل خروج
        if (error.message?.includes('timeout') || error.message?.includes('network') || error.code === 'PGRST301') {
          console.error('❌ Network/Timeout error - signing out');
          setTimeoutOccurred(true);
          return null;
        }
        // للأخطاء الأخرى، نستخدم fallback
        console.log('✅ Using fallback user (error)');
        return fallbackUser;
      }

      if (data) {
        console.log('✅✅✅ Found user in database:', data);
        return data as User;
      }

      // إذا لم يوجد data، استخدم fallback
      console.log('⚠️ No data returned - using fallback user');
      return fallbackUser;
      
    } catch (error: any) {
      console.error('❌❌❌ Exception in fetchUserProfile:', error);
      
      // حتى لو حدث error، أرجع fallback user
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session) {
        return {
          id: sessionData.session.user.id,
          email: sessionData.session.user.email || email,
          full_name: null,
          phone: null,
          role: null,
          avatar_url: null,
          is_profile_complete: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      
      return null;
    }
  };

  const refreshUser = async () => {
    if (session?.user?.email) {
      const profile = await fetchUserProfile(session.user.email);
      setUser(profile);
    }
  };

  useEffect(() => {
    // Set up Supabase token getter for API calls
    setSupabaseTokenGetter(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    });

    // Get initial session
    console.log('AuthContext: Initializing...');
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;

    const initAuth = async () => {
      try {
        // Check for dev login first (localhost only)
        if (isDevMode && localStorage.getItem('dev_logged_in') === 'true' && getAuthToken()) {
          console.log('🔓 Restoring dev login session');
          try {
            // Verify token is still valid by fetching current user
            const userData = await authApi.getCurrentUser();
            if (userData) {
              const devUser: User = {
                id: userData.id,
                email: userData.email,
                full_name: userData.full_name,
                phone: userData.phone,
                role: userData.role,
                avatar_url: userData.avatar_url,
                is_profile_complete: userData.is_profile_complete,
                is_super_admin: userData.is_super_admin,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
              setUser(devUser);
              setIsDevLoggedIn(true);
              setLoading(false);
              return;
            }
          } catch (e) {
            // Token invalid, clear dev login
            console.log('🔓 Dev session expired, clearing...');
            localStorage.removeItem('dev_logged_in');
            setAuthToken(null);
          }
        }

        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('Initial session:', session, 'Error:', error);

        if (!isMounted) return;

        setSession(session);

        if (session?.user?.email) {
          console.log('Fetching profile for email:', session.user.email);
          
          // Set timeout للـ loading - إذا حدث timeout، نسجل خروج
          timeoutId = setTimeout(async () => {
            if (!isMounted) return;
            console.error('⏱️ Initialization timeout - signing out');
            setTimeoutOccurred(true);
            await signOut();
            if (isMounted) {
              window.location.href = '/auth/login';
            }
          }, 10000);
          
          fetchUserProfile(session.user.email).then((profile) => {
            if (!isMounted) return;
            
            if (timeoutId) clearTimeout(timeoutId);
            
            if (profile === null && timeoutOccurred) {
              // Timeout occurred, sign out
              signOut().then(() => {
                if (isMounted) {
                  window.location.href = '/auth/login';
                }
              });
              return;
            }
            console.log('Profile fetched:', profile);
            if (isMounted) {
              setUser(profile);
              setLoading(false);
            }
          }).catch(async (err) => {
            if (!isMounted) return;
            
            if (timeoutId) clearTimeout(timeoutId);
            console.error('Error fetching profile:', err);
            // إذا كان error بسبب timeout، نسجل خروج
            if (err.message?.includes('timeout') || timeoutOccurred) {
              await signOut();
              if (isMounted) {
                window.location.href = '/auth/login';
              }
            } else {
              if (isMounted) {
                setLoading(false);
              }
            }
          });
        } else {
          if (isMounted) {
            setLoading(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error initializing auth:', error);
          setLoading(false);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };

    // Listen for auth changes
    let authChangeTimeoutId: NodeJS.Timeout | null = null;
    
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log('Auth state changed:', event, session);
      setSession(session);
      
      if (authChangeTimeoutId) {
        clearTimeout(authChangeTimeoutId);
      }
      
      if (session?.user?.email) {
        console.log('Fetching profile after auth change:', session.user.email);
        
        // Set timeout - إذا حدث timeout، نسجل خروج
        authChangeTimeoutId = setTimeout(async () => {
          if (!isMounted) return;
          console.error('⏱️ Auth change timeout - signing out');
          setTimeoutOccurred(true);
          await signOut();
          if (isMounted) {
            window.location.href = '/auth/login';
          }
        }, 10000);
        
        try {
          const profile = await fetchUserProfile(session.user.email);
          
          if (!isMounted) return;
          
          if (authChangeTimeoutId) clearTimeout(authChangeTimeoutId);
          
          if (profile === null && timeoutOccurred) {
            // Timeout occurred, sign out
            await signOut();
            if (isMounted) {
              window.location.href = '/auth/login';
            }
            return;
          }
          
          console.log('Profile after auth change:', profile);
          if (isMounted) {
            setUser(profile);
            setLoading(false);
          }
        } catch (error: any) {
          if (!isMounted) return;
          
          if (authChangeTimeoutId) clearTimeout(authChangeTimeoutId);
          console.error('Error fetching profile after auth change:', error);
          // إذا كان error بسبب timeout، نسجل خروج
          if (error?.message?.includes('timeout') || timeoutOccurred) {
            await signOut();
            if (isMounted) {
              window.location.href = '/auth/login';
            }
          } else {
            if (isMounted) {
              setLoading(false);
            }
          }
        }
      } else {
        if (isMounted) {
          setUser(null);
          setLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (authChangeTimeoutId) clearTimeout(authChangeTimeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            hd: '24f.co' // Restrict to 24f.co domain
          }
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear dev login state
      if (isDevLoggedIn) {
        localStorage.removeItem('dev_logged_in');
        setAuthToken(null); // Clear JWT token
        setIsDevLoggedIn(false);
        setUser(null);
        setSession(null);
        return;
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setAuthToken(null); // Clear JWT token
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // Dev login for localhost testing only
  const devLogin = async (email: string, password: string): Promise<boolean> => {
    if (!isDevMode) {
      console.error('Dev login is only available on localhost');
      return false;
    }

    try {
      console.log('🔓 Attempting dev login via API...');
      const result = await authApi.devLogin(email, password);

      if (result?.token && result?.user) {
        console.log('🔓 Dev login successful');

        const devUser: User = {
          id: result.user.id,
          email: result.user.email,
          full_name: result.user.full_name,
          phone: null,
          role: result.user.role,
          avatar_url: null,
          is_profile_complete: result.user.is_profile_complete,
          is_super_admin: result.user.is_super_admin,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setUser(devUser);
        setIsDevLoggedIn(true);
        localStorage.setItem('dev_logged_in', 'true');
        setLoading(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Dev login failed:', error);
      return false;
    }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    // Check for user email from session or user object
    const userEmail = session?.user?.email || user?.email;

    if (!userEmail) {
      throw new Error('No user session found. Please sign in again.');
    }

    try {
      // Use the backend API to update profile (uses supabaseAdmin to bypass RLS)
      const updatedUser = await authApi.updateProfile({
        full_name: data.full_name,
        phone: data.phone,
        role: data.role,
      });

      if (updatedUser) {
        // Update local user state with the returned data
        setUser(prev => prev ? { ...prev, ...updatedUser, is_profile_complete: true } : prev);
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const value = {
    session,
    user,
    loading,
    signInWithGoogle,
    signOut,
    updateUserProfile,
    refreshUser,
    devLogin,
    isDevMode
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

